import { cn } from "../lib/utils";

/**
 * Zeel AI brand mark — bracket-AI glyph. Used anywhere we need to signal
 * "this is AI-sourced content" (card headers, avatar overlays, filter chips).
 *
 * Props:
 *  - className   : size / colour overrides on the outer svg
 *  - variant     :
 *      "solid" (default) — indigo→violet→purple FILLED rounded square with
 *                          white "AI" text. Best for small sizes (12–18px)
 *                          where a stroked glyph is too thin to read.
 *      "brand"           — gradient stroked outline + gradient-filled "AI".
 *      "mono"            — inherits `currentColor` for stroke/fill.
 *  - withSparkle : show the small decorative sparkle (default true)
 */
export function AiMark({ className, variant = "solid", withSparkle = true, ...rest }) {
  const gradientId = `zeel-ai-mark-g-${variant}`;
  const useGradient = variant !== "mono";

  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("w-4 h-4", className)}
      aria-hidden="true"
      {...rest}
    >
      {useGradient && (
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366F1" />
            <stop offset="50%" stopColor="#8B5CF6" />
            <stop offset="100%" stopColor="#A855F7" />
          </linearGradient>
        </defs>
      )}

      {variant === "solid" ? (
        <>
          {/* filled rounded square */}
          <rect
            x="3"
            y="3"
            width="18"
            height="18"
            rx="4"
            fill={useGradient ? `url(#${gradientId})` : "currentColor"}
          />
          {/* white "AI" */}
          <text
            x="12"
            y="16.5"
            textAnchor="middle"
            fontSize="10.5"
            fontWeight="900"
            fontFamily="Inter, sans-serif"
            fill="#ffffff"
            letterSpacing="-0.04em"
          >
            AI
          </text>
          {withSparkle && (
            <path
              d="M19.5 1 l.75 1.75 L22 3.5 l-1.75.75 L19.5 6 l-.75-1.75 L17 3.5 l1.75-.75 Z"
              fill={useGradient ? `url(#${gradientId})` : "currentColor"}
            />
          )}
        </>
      ) : (
        <>
          {/* stroked outline — original variant */}
          <rect
            x="3.25"
            y="3"
            width="17.5"
            height="18"
            rx="3"
            fill="none"
            stroke={useGradient ? `url(#${gradientId})` : "currentColor"}
            strokeWidth="1.8"
          />
          <text
            x="12"
            y="16"
            textAnchor="middle"
            fontSize="9.5"
            fontWeight="800"
            fontFamily="Inter, sans-serif"
            fill={useGradient ? `url(#${gradientId})` : "currentColor"}
          >
            AI
          </text>
          {withSparkle && (
            <path
              d="M19 1.5 l.85 2 L22 4.35 l-2 .85 L19 7.2 l-.85-2 L16 4.35 l2-.85 Z"
              fill={useGradient ? `url(#${gradientId})` : "currentColor"}
            />
          )}
        </>
      )}
    </svg>
  );
}

export default AiMark;
